import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  LogOut, Plus, Search, Filter, Edit2, Trash2, 
  Phone, Mail, MapPin, AlertCircle, FileText, 
  Upload, X, Save, Building2, Users, Briefcase, Settings, Download,
  CheckCircle, XCircle, Info, AlertTriangle, Wrench, ArrowUpDown, ArrowUp, ArrowDown, Star, Tag
} from 'lucide-react';
import './Dashboard.css';

// ==================== VERSION ====================
// Hier kannst du die Version nach jeder Änderung anpassen
const APP_VERSION = "2.4.0";
const VERSION_DATE = "26.11.2025";
// =================================================

// Custom Confirmation Modal Component
function ConfirmModal({ show, title, message, confirmText, cancelText, onConfirm, onCancel, type = 'danger' }) {
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && show) {
        onCancel();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [show, onCancel]);

  if (!show) return null;
  const typeColors = {
    danger: { bg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', icon: '🗑️' },
    warning: { bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', icon: '⚠️' },
    info: { bg: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', icon: 'ℹ️' }
  };
  const colors = typeColors[type] || typeColors.danger;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, animation: 'fadeIn 0.2s ease' }}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '32px', maxWidth: '450px', width: '90%', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)', animation: 'slideUp 0.3s ease' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: colors.bg, margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px' }}>{colors.icon}</div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 8px 0' }}>{title}</h2>
          <p style={{ fontSize: '16px', color: '#666', margin: 0, lineHeight: '1.5' }}>{message}</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
          <button onClick={onConfirm} style={{ padding: '14px 24px', background: colors.bg, color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease' }}>{confirmText || 'Bestätigen'}</button>
          <button onClick={onCancel} style={{ padding: '14px 24px', background: 'white', color: '#666', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease' }}>{cancelText || 'Abbrechen'}</button>
        </div>
      </div>
    </div>
  );
}

// Toast Notification Component
function Toast({ message, type = 'success', show, onClose }) {
  React.useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 4000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);
  if (!show) return null;
  const typeConfig = {
    success: { bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', icon: <CheckCircle size={20} /> },
    error: { bg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', icon: <XCircle size={20} /> },
    info: { bg: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', icon: <Info size={20} /> },
    warning: { bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', icon: <AlertTriangle size={20} /> }
  };
  const config = typeConfig[type] || typeConfig.success;
  return (
    <div style={{ position: 'fixed', top: '20px', right: '20px', background: config.bg, color: 'white', padding: '16px 24px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)', zIndex: 10001, display: 'flex', alignItems: 'center', gap: '12px', minWidth: '300px', maxWidth: '500px', animation: 'slideInRight 0.3s ease' }}>
      {config.icon}
      <span style={{ flex: 1, fontSize: '14px', fontWeight: '500' }}>{message}</span>
      <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}><X size={18} /></button>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [firmen, setFirmen] = useState([]);
  const [gewerke, setGewerke] = useState([]);
  const [filialen, setFilialen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGewerk, setSelectedGewerk] = useState('');
  const [selectedFiliale, setSelectedFiliale] = useState('');
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [showOnlyRahmenvertrag, setShowOnlyRahmenvertrag] = useState(false);
  const [selectedPrioritaet, setSelectedPrioritaet] = useState('');
  
  // Sort states
  const [sortBy, setSortBy] = useState('name'); // 'name', 'created', 'updated', 'prioritaet'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  
  // Modal states - Firmen
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingFirma, setEditingFirma] = useState(null);
  const [viewingFirma, setViewingFirma] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    adresse: '',
    telefon: '',
    email: '',
    notiz: '',
    notfall_service: false,
    rahmenvertrag: false,
    ist_favorit: false,
    prioritaet: 'standard',
    vertragsnummer: '',
    vertragslaufzeit: '',
    kuendigungsfrist: '',
    ansprechperson1_name: '',
    ansprechperson1_position: '',
    ansprechperson1_telefon: '',
    ansprechperson1_email: '',
    ansprechperson2_name: '',
    ansprechperson2_position: '',
    ansprechperson2_telefon: '',
    ansprechperson2_email: '',
    ansprechperson3_name: '',
    ansprechperson3_position: '',
    ansprechperson3_telefon: '',
    ansprechperson3_email: '',
    ansprechperson4_name: '',
    ansprechperson4_position: '',
    ansprechperson4_telefon: '',
    ansprechperson4_email: '',
    ansprechperson5_name: '',
    ansprechperson5_position: '',
    ansprechperson5_telefon: '',
    ansprechperson5_email: '',
    gewerk_ids: [],
    filiale_ids: []
  });

  // Modal states - Filialen
  const [showFilialeModal, setShowFilialeModal] = useState(false);
  const [editingFiliale, setEditingFiliale] = useState(null);
  const [filialeFormData, setFilialeFormData] = useState({
    gebiet: '',
    filialnummer: '',
    name: '',
    adresse: '',
    plz: '',
    ort: ''
  });

  // Modal states - Gewerke
  const [showGewerkeModal, setShowGewerkeModal] = useState(false);
  const [editingGewerk, setEditingGewerk] = useState(null);
  const [gewerkeFormData, setGewerkeFormData] = useState({
    name: '',
    beschreibung: ''
  });

  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', confirmText: '', onConfirm: null, type: 'danger' });
  
  // Toast State
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Refs für Modal-Close Tracking
  const modalOverlayMouseDown = useRef(false);
  const editModalRef = useRef(null);
  const detailModalRef = useRef(null);
  const filialeModalRef = useRef(null);
  const gewerkeModalRef = useRef(null);
  
  // Refs für erste Input-Felder
  const firstInputRef = useRef(null);
  const filialeFirstInputRef = useRef(null);
  const gewerkeFirstInputRef = useRef(null);

  const showToast = (message, type = 'success') => setToast({ show: true, message, type });
  const hideToast = () => setToast({ show: false, message: '', type: 'success' });
  const showConfirm = (title, message, onConfirm, confirmText = 'Bestätigen', type = 'danger') => setConfirmModal({ show: true, title, message, confirmText, onConfirm, type });
  const hideConfirm = () => setConfirmModal({ show: false, title: '', message: '', confirmText: '', onConfirm: null, type: 'danger' });


  // ESC Handler für Modals
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (showModal) handleCloseModal();
        if (showDetailModal) handleCloseDetailModal();
        if (showFilialeModal) handleCloseFilialeModal();
        if (showGewerkeModal) handleCloseGewerkeModal();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showModal, showDetailModal, showFilialeModal, showGewerkeModal]);

  // Auto-Fokus für Modals (ermöglicht sofortiges Text-Markieren/Bearbeiten)
  useEffect(() => {
    if (showModal && firstInputRef.current) {
      // Fokussiere das erste Input-Feld im Edit Modal
      firstInputRef.current.focus();
    }
  }, [showModal]);

  useEffect(() => {
    if (showDetailModal && detailModalRef.current) {
      // Fokussiere das Modal-Content im Detail Modal (nur zum Markieren)
      detailModalRef.current.focus();
    }
  }, [showDetailModal]);

  useEffect(() => {
    if (showFilialeModal && filialeFirstInputRef.current) {
      // Fokussiere das erste Input-Feld im Filialen Modal
      filialeFirstInputRef.current.focus();
    }
  }, [showFilialeModal]);

  useEffect(() => {
    if (showGewerkeModal && gewerkeFirstInputRef.current) {
      // Fokussiere das erste Input-Feld im Gewerke Modal
      gewerkeFirstInputRef.current.focus();
    }
  }, [showGewerkeModal]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadFirmen();
  }, [selectedGewerk, selectedFiliale, searchTerm]);

  const loadData = async () => {
    try {
      const [gewerkeRes, filialenRes] = await Promise.all([
        axios.get('/gewerke'),
        axios.get('/filialen')
      ]);
      setGewerke(gewerkeRes.data);
      setFilialen(filialenRes.data);
      await loadFirmen();
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadFirmen = async () => {
    try {
      const params = {};
      if (selectedGewerk) params.gewerk_id = selectedGewerk;
      if (selectedFiliale) params.filiale_id = selectedFiliale;
      if (searchTerm) params.search = searchTerm;
      
      const response = await axios.get('/firmen', { params });
      setFirmen(response.data);
    } catch (err) {
      setError('Fehler beim Laden der Firmen');
      console.error(err);
    }
  };

  const loadFilialen = async () => {
    try {
      const response = await axios.get('/filialen');
      setFilialen(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadGewerke = async () => {
    try {
      const response = await axios.get('/gewerke');
      setGewerke(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  // Clear Filter Functions
  const clearSearchFilter = () => {
    setSearchTerm('');
  };

  const clearGewerkeFilter = () => {
    setSelectedGewerk('');
  };

  const clearFilialeFilter = () => {
    setSelectedFiliale('');
  };

  // Sort Functions
  const getSortedFirmen = () => {
    let filtered = [...firmen];
    
    // Filter: Nur Favoriten anzeigen
    if (showOnlyFavorites) {
      filtered = filtered.filter(f => f.ist_favorit);
    }
    
    // Filter: Nur Rahmenverträge anzeigen
    if (showOnlyRahmenvertrag) {
      filtered = filtered.filter(f => f.rahmenvertrag);
    }
    
    // Filter: Nach Priorität
    if (selectedPrioritaet) {
      filtered = filtered.filter(f => f.prioritaet === selectedPrioritaet);
    }
    
    // Sortierung
    filtered.sort((a, b) => {
      let compareValue = 0;
      
      switch (sortBy) {
        case 'name':
          compareValue = a.name.localeCompare(b.name, 'de');
          break;
        case 'created':
          compareValue = new Date(a.created_at) - new Date(b.created_at);
          break;
        case 'updated':
          compareValue = new Date(a.updated_at) - new Date(b.updated_at);
          break;
        case 'prioritaet':
          const priorityOrder = { 'bevorzugt': 1, 'standard': 2, 'backup': 3, 'notfall': 4, 'keine': 5 };
          compareValue = (priorityOrder[a.prioritaet] || 5) - (priorityOrder[b.prioritaet] || 5);
          break;
        default:
          compareValue = 0;
      }
      
      return sortOrder === 'asc' ? compareValue : -compareValue;
    });
    
    return filtered;
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // Priorität Badge Helper
  const getPrioritaetBadge = (prioritaet) => {
    const badges = {
      'bevorzugt': { label: 'Bevorzugt', className: 'badge-bevorzugt' },
      'standard': { label: 'Standard', className: 'badge-standard' },
      'backup': { label: 'Backup', className: 'badge-backup' },
      'notfall': { label: 'Nur im Notfall', className: 'badge-notfall-prio' },
      'keine': { label: 'Keine Bewertung', className: 'badge-keine' }
    };
    return badges[prioritaet] || badges['standard'];
  };

  // Detail Modal Functions
  const handleOpenDetailModal = async (firma) => {
    try {
      const response = await axios.get(`/firmen/${firma.id}`);
      setViewingFirma(response.data);
      setShowDetailModal(true);
    } catch (err) {
      showToast('Fehler beim Laden der Details', 'error');
    }
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setViewingFirma(null);
  };

  // Firmen Modal Functions
  const handleOpenModal = (firma = null) => {
    if (firma) {
      setEditingFirma(firma);
      setFormData({
        ...firma,
        gewerk_ids: firma.gewerke.map(g => g.id),
        filiale_ids: firma.filialen.map(f => f.id)
      });
    } else {
      setEditingFirma(null);
      setFormData({
        name: '',
        adresse: '',
        telefon: '',
        email: '',
        notiz: '',
        notfall_service: false,
        rahmenvertrag: false,
        ist_favorit: false,
        prioritaet: 'standard',
        vertragsnummer: '',
        vertragslaufzeit: '',
        kuendigungsfrist: '',
        ansprechperson1_name: '',
        ansprechperson1_position: '',
        ansprechperson1_telefon: '',
        ansprechperson1_email: '',
        ansprechperson2_name: '',
        ansprechperson2_position: '',
        ansprechperson2_telefon: '',
        ansprechperson2_email: '',
        ansprechperson3_name: '',
        ansprechperson3_position: '',
        ansprechperson3_telefon: '',
        ansprechperson3_email: '',
        ansprechperson4_name: '',
        ansprechperson4_position: '',
        ansprechperson4_telefon: '',
        ansprechperson4_email: '',
        ansprechperson5_name: '',
        ansprechperson5_position: '',
        ansprechperson5_telefon: '',
        ansprechperson5_email: '',
        gewerk_ids: [],
        filiale_ids: []
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingFirma(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFirma) {
        await axios.put(`/firmen/${editingFirma.id}`, formData);
      } else {
        const response = await axios.post('/firmen', formData);
        setEditingFirma(response.data);
      }
      loadFirmen();
      handleCloseModal(); // Fenster schließen nach erfolgreichem Speichern
      if (!editingFirma) {
        showToast('Firma erfolgreich gespeichert! Sie können jetzt Dateien hochladen.', 'success');
      }
    } catch (err) {
      showToast('Fehler beim Speichern: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleFileUpload = async (e) => {
    if (!editingFirma) {
      showToast('Bitte speichern Sie die Firma zuerst!', 'warning');
      return;
    }

    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`/firmen/${editingFirma.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const response = await axios.get(`/firmen/${editingFirma.id}`);
      setEditingFirma(response.data);
      
      // Auch die Firmenliste aktualisieren
      loadFirmen();
      
      showToast('Datei erfolgreich hochgeladen!', 'success');
      e.target.value = '';
    } catch (err) {
      showToast('Fehler beim Hochladen: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleFileDelete = async (dokumentId, filename) => {
    if (!editingFirma) return;

    showConfirm(
      'Dokument löschen',
      `Möchten Sie das Dokument "${filename}" wirklich löschen?`,
      async () => {
        try {
          await axios.delete(`/firmen/${editingFirma.id}/dokumente/${dokumentId}`);
          
          const response = await axios.get(`/firmen/${editingFirma.id}`);
          setEditingFirma(response.data);
          
          // Auch die Firmenliste aktualisieren, damit die Änderung überall sichtbar ist
          loadFirmen();
          
          showToast('Dokument erfolgreich gelöscht', 'success');
          hideConfirm();
        } catch (err) {
          showToast('Fehler beim Löschen: ' + (err.response?.data?.detail || err.message), 'error');
          hideConfirm();
        }
      },
      'Löschen',
      'danger'
    );
  };

  const handleFileDownload = async (dokumentId, filename) => {
    if (!editingFirma) return;

    try {
      const response = await axios.get(`/firmen/${editingFirma.id}/dokumente/${dokumentId}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showToast('Download gestartet', 'success');
    } catch (err) {
      showToast('Fehler beim Download: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleDelete = async (id, name) => {
    showConfirm(
      'Firma löschen',
      `Möchten Sie die Firma "${name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      async () => {
        try {
          await axios.delete(`/firmen/${id}`);
          loadFirmen();
          showToast('Firma erfolgreich gelöscht', 'success');
          hideConfirm();
        } catch (err) {
          showToast('Fehler beim Löschen', 'error');
          hideConfirm();
        }
      },
      'Löschen',
      'danger'
    );
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMultiSelect = (name, value) => {
    setFormData(prev => {
      const currentValues = prev[name] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [name]: newValues };
    });
  };

  // Filialen Modal Functions
  const handleOpenFilialeModal = (filiale = null) => {
    if (filiale) {
      setEditingFiliale(filiale);
      setFilialeFormData({
        gebiet: filiale.gebiet,
        filialnummer: filiale.filialnummer,
        name: filiale.name,
        adresse: filiale.adresse || '',
        plz: filiale.plz || '',
        ort: filiale.ort || ''
      });
    } else {
      setEditingFiliale(null);
      setFilialeFormData({
        gebiet: '',
        filialnummer: '',
        name: '',
        adresse: '',
        plz: '',
        ort: ''
      });
    }
    setShowFilialeModal(true);
  };

  const handleCloseFilialeModal = () => {
    setShowFilialeModal(false);
    setEditingFiliale(null);
  };

  const handleFilialeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFiliale) {
        await axios.put(`/filialen/${editingFiliale.id}`, filialeFormData);
      } else {
        await axios.post('/filialen', filialeFormData);
      }
      handleCloseFilialeModal();
      loadFilialen();
      handleCloseFilialeModal(); // Fenster schließen nach Speichern
    } catch (err) {
      showToast('Fehler beim Speichern: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleFilialeDelete = async (id, name) => {
    showConfirm(
      'Filiale löschen',
      `Möchten Sie die Filiale "${name}" wirklich löschen? Alle Zuordnungen zu Firmen gehen verloren!`,
      async () => {
        try {
          await axios.delete(`/filialen/${id}`);
          loadFilialen();
          showToast('Filiale erfolgreich gelöscht', 'success');
          hideConfirm();
        } catch (err) {
          showToast('Fehler beim Löschen', 'error');
          hideConfirm();
        }
      },
      'Löschen',
      'danger'
    );
  };

  const handleFilialeInputChange = (e) => {
    const { name, value } = e.target;
    setFilialeFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Gewerke Modal Functions
  const handleOpenGewerkeModal = (gewerk = null) => {
    if (gewerk) {
      setEditingGewerk(gewerk);
      setGewerkeFormData({
        name: gewerk.name,
        beschreibung: gewerk.beschreibung || ''
      });
    } else {
      setEditingGewerk(null);
      setGewerkeFormData({
        name: '',
        beschreibung: ''
      });
    }
    setShowGewerkeModal(true);
  };

  const handleCloseGewerkeModal = () => {
    setShowGewerkeModal(false);
    setEditingGewerk(null);
  };

  const handleGewerkeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGewerk) {
        await axios.put(`/gewerke/${editingGewerk.id}`, gewerkeFormData);
        showToast('Gewerk erfolgreich aktualisiert', 'success');
      } else {
        await axios.post('/gewerke', gewerkeFormData);
        showToast('Gewerk erfolgreich hinzugefügt', 'success');
      }
      loadGewerke();
      handleCloseGewerkeModal(); // Fenster schließen nach Speichern
    } catch (err) {
      showToast('Fehler beim Speichern: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleGewerkeDelete = async (id, name) => {
    showConfirm(
      'Gewerk löschen',
      `Möchten Sie das Gewerk "${name}" wirklich löschen? Alle Zuordnungen zu Firmen gehen verloren!`,
      async () => {
        try {
          await axios.delete(`/gewerke/${id}`);
          loadGewerke();
          showToast('Gewerk erfolgreich gelöscht', 'success');
          hideConfirm();
        } catch (err) {
          showToast('Fehler beim Löschen', 'error');
          hideConfirm();
        }
      },
      'Löschen',
      'danger'
    );
  };

  const handleGewerkeInputChange = (e) => {
    const { name, value } = e.target;
    setGewerkeFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getFilteredStats = () => {
    return {
      total: firmen.length,
      notfall: firmen.filter(f => f.notfall_service).length
    };
  };

  const stats = getFilteredStats();

  if (loading) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="dashboard">
      <ConfirmModal {...confirmModal} onCancel={hideConfirm} />
      <Toast {...toast} onClose={hideToast} />
      
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <Building2 size={32} />
            <div>
              <h1>Handwerker Verwaltung</h1>
              <p>{stats.total} Firmen • {stats.notfall} mit 24/7 Service • {filialen.length} Filialen</p>
            </div>
          </div>
          <div className="header-actions">
            <div className="version-badge">
              <Tag size={14} />
              <span>v{APP_VERSION}</span>
              <span className="version-date">{VERSION_DATE}</span>
            </div>
            <button onClick={() => handleOpenGewerkeModal()} className="settings-btn">
              <Wrench size={20} />
              Gewerke verwalten
            </button>
            <button onClick={() => setShowFilialeModal(true)} className="settings-btn">
              <Settings size={20} />
              Filialen verwalten
            </button>
            <button onClick={onLogout} className="logout-btn">
              <LogOut size={20} />
              Abmelden
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="controls-bar">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Suche nach Name, Adresse, Ansprechpartner, Filiale..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                className="clear-filter-btn" 
                onClick={clearSearchFilter}
                title="Suche löschen"
              >
                <X size={16} />
              </button>
            )}
          </div>

          <div className="filters">
            <div className="filter-group">
              <Filter size={18} />
              <select 
                value={selectedGewerk} 
                onChange={(e) => setSelectedGewerk(e.target.value)}
              >
                <option value="">Alle Gewerke</option>
                {gewerke.map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
              {selectedGewerk && (
                <button 
                  className="clear-filter-btn" 
                  onClick={clearGewerkeFilter}
                  title="Filter löschen"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <div className="filter-group">
              <MapPin size={18} />
              <select 
                value={selectedFiliale} 
                onChange={(e) => setSelectedFiliale(e.target.value)}
              >
                <option value="">Alle Filialen</option>
                {filialen.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.filialnummer} - {f.name}
                  </option>
                ))}
              </select>
              {selectedFiliale && (
                <button 
                  className="clear-filter-btn" 
                  onClick={clearFilialeFilter}
                  title="Filter löschen"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <div className="filter-group">
              <Filter size={18} />
              <select 
                value={selectedPrioritaet} 
                onChange={(e) => setSelectedPrioritaet(e.target.value)}
              >
                <option value="">Alle Prioritäten</option>
                <option value="bevorzugt">🟢 Bevorzugt</option>
                <option value="standard">🟡 Standard</option>
                <option value="backup">🟠 Backup</option>
                <option value="notfall">🔴 Nur im Notfall</option>
                <option value="keine">⚪ Keine Bewertung</option>
              </select>
              {selectedPrioritaet && (
                <button 
                  className="clear-filter-btn" 
                  onClick={() => setSelectedPrioritaet('')}
                  title="Filter löschen"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <button 
              className={`filter-btn ${showOnlyFavorites ? 'active' : ''}`}
              onClick={() => setShowOnlyFavorites(!showOnlyFavorites)}
              title="Nur Favoriten anzeigen"
            >
              <Star size={18} fill={showOnlyFavorites ? "#fbbf24" : "none"} color={showOnlyFavorites ? "#fbbf24" : "currentColor"} />
              {showOnlyFavorites ? 'Favoriten' : 'Alle'}
            </button>

            <button 
              className={`filter-btn ${showOnlyRahmenvertrag ? 'active' : ''}`}
              onClick={() => setShowOnlyRahmenvertrag(!showOnlyRahmenvertrag)}
              title="Nur Firmen mit Rahmenvertrag"
            >
              <FileText size={18} />
              {showOnlyRahmenvertrag ? 'Rahmenvertrag' : 'Alle'}
            </button>
          </div>

          <div className="filter-group">
            <ArrowUpDown size={18} />
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name">Nach Name</option>
              <option value="prioritaet">Nach Priorität</option>
              <option value="created">Nach Erstelldatum</option>
              <option value="updated">Nach Änderungsdatum</option>
            </select>
            <button 
              className="sort-order-btn" 
              onClick={toggleSortOrder}
              title={sortOrder === 'asc' ? 'Aufsteigend' : 'Absteigend'}
            >
              {sortOrder === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
            </button>
          </div>

          <button onClick={() => handleOpenModal()} className="add-btn">
            <Plus size={20} />
            Neue Firma
          </button>
        </div>

        {error && (
          <div className="error-banner">
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        <div className="firmen-grid">
          {getSortedFirmen().map(firma => (
            <div key={firma.id} className="firma-card" onClick={() => handleOpenDetailModal(firma)}>
              <div className="firma-header">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 1, minWidth: 0 }}>
                      <h3 style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{firma.name}</h3>
                      {firma.ist_favorit && (
                        <Star size={20} fill="#fbbf24" color="#fbbf24" style={{ flexShrink: 0 }} />
                      )}
                    </div>
                    <div className="firma-actions" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => handleOpenModal(firma)} className="icon-btn">
                        <Edit2 size={18} />
                      </button>
                      <button onClick={() => handleDelete(firma.id, firma.name)} className="icon-btn icon-btn-danger">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {firma.prioritaet && firma.prioritaet !== 'standard' && (
                      <span className={`badge ${getPrioritaetBadge(firma.prioritaet).className}`}>
                        {getPrioritaetBadge(firma.prioritaet).label}
                      </span>
                    )}
                    {firma.notfall_service && (
                      <span className="badge badge-notfall">24/7 Service</span>
                    )}
                    {firma.rahmenvertrag && (
                      <span className="badge badge-rahmenvertrag">
                        <FileText size={14} style={{ marginRight: '4px' }} />
                        Rahmenvertrag
                      </span>
                    )}
                    {firma.dokumente && firma.dokumente.length > 0 && (
                      <span className="badge badge-docs">{firma.dokumente.length} Dateien</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="firma-body">
                {firma.adresse && (
                  <div className="info-row">
                    <MapPin size={16} />
                    <span>{firma.adresse}</span>
                  </div>
                )}
                {firma.telefon && (
                  <div className="info-row">
                    <Phone size={16} />
                    <a href={`tel:${firma.telefon}`} onClick={(e) => e.stopPropagation()}>{firma.telefon}</a>
                  </div>
                )}
                {firma.email && (
                  <div className="info-row">
                    <Mail size={16} />
                    <a href={`mailto:${firma.email}`} onClick={(e) => e.stopPropagation()}>{firma.email}</a>
                  </div>
                )}

                {firma.gewerke.length > 0 && (
                  <div className="tags-section">
                    <Briefcase size={14} />
                    <div className="tags">
                      {firma.gewerke.map(g => (
                        <span key={g.id} className="tag tag-gewerk">{g.name}</span>
                      ))}
                    </div>
                  </div>
                )}

                {firma.filialen.length > 0 && (
                  <div className="tags-section">
                    <Building2 size={14} />
                    <div className="tags">
                      {firma.filialen.slice(0, 3).map(f => (
                        <span 
                          key={f.id} 
                          className="tag tag-filiale"
                          title={f.name}
                        >
                          {f.filialnummer}
                        </span>
                      ))}
                      {firma.filialen.length > 3 && (
                        <span 
                          className="tag tag-more"
                          title={`Weitere Filialen: ${firma.filialen.slice(3).map(f => `${f.filialnummer} - ${f.name}`).join(', ')}`}
                        >
                          +{firma.filialen.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {firmen.length === 0 && (
          <div className="empty-state">
            <Users size={48} />
            <h3>Keine Firmen gefunden</h3>
            <p>Versuchen Sie andere Filter oder fügen Sie eine neue Firma hinzu</p>
          </div>
        )}
      </div>

      {/* Detail Modal (NUR ANSEHEN) */}
      {showDetailModal && viewingFirma && (
        <div 
          className="modal-overlay" 
          onMouseDown={(e) => {
            modalOverlayMouseDown.current = (e.target === e.currentTarget);
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && modalOverlayMouseDown.current) {
              handleCloseDetailModal();
            }
            modalOverlayMouseDown.current = false;
          }}
        >
          <div 
            ref={detailModalRef}
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
          >
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h2>{viewingFirma.name}</h2>
                {viewingFirma.ist_favorit && (
                  <Star size={24} fill="#fbbf24" color="#fbbf24" />
                )}
              </div>
              <div className="modal-header-actions">
                <button onClick={() => {
                  handleCloseDetailModal();
                  handleOpenModal(viewingFirma);
                }} className="icon-btn-edit">
                  <Edit2 size={20} />
                  Bearbeiten
                </button>
                <button onClick={handleCloseDetailModal} className="close-btn">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="detail-view">
              <div className="detail-section">
                <h4>Grunddaten</h4>
                <div className="detail-grid">
                  {viewingFirma.adresse && (
                    <div className="detail-item">
                      <MapPin size={18} />
                      <div>
                        <label>Adresse</label>
                        <span>{viewingFirma.adresse}</span>
                      </div>
                    </div>
                  )}
                  {viewingFirma.telefon && (
                    <div className="detail-item">
                      <Phone size={18} />
                      <div>
                        <label>Telefon</label>
                        <a href={`tel:${viewingFirma.telefon}`}>{viewingFirma.telefon}</a>
                      </div>
                    </div>
                  )}
                  {viewingFirma.email && (
                    <div className="detail-item">
                      <Mail size={18} />
                      <div>
                        <label>Email</label>
                        <a href={`mailto:${viewingFirma.email}`}>{viewingFirma.email}</a>
                      </div>
                    </div>
                  )}
                  {viewingFirma.notfall_service && (
                    <div className="detail-item">
                      <AlertCircle size={18} />
                      <div>
                        <label>Service</label>
                        <span className="badge badge-notfall">24/7 Notfall-Service</span>
                      </div>
                    </div>
                  )}
                  {viewingFirma.rahmenvertrag && (
                    <div className="detail-item">
                      <FileText size={18} />
                      <div>
                        <label>Vertrag</label>
                        <span className="badge badge-rahmenvertrag">Rahmenvertrag vorhanden</span>
                      </div>
                    </div>
                  )}
                  {viewingFirma.prioritaet && viewingFirma.prioritaet !== 'standard' && (
                    <div className="detail-item">
                      <Star size={18} />
                      <div>
                        <label>Priorität</label>
                        <span className={`badge ${getPrioritaetBadge(viewingFirma.prioritaet).className}`}>
                          {getPrioritaetBadge(viewingFirma.prioritaet).label}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {(viewingFirma.vertragsnummer || viewingFirma.vertragslaufzeit || viewingFirma.kuendigungsfrist) && (

                <div className="detail-section">
                  <h4>Vertragsdaten</h4>
                  <div className="detail-grid">
                    {viewingFirma.vertragsnummer && (
                      <div className="detail-item">
                        <FileText size={18} />
                        <div>
                          <label>Vertragsnummer</label>
                          <span>{viewingFirma.vertragsnummer}</span>
                        </div>
                      </div>
                    )}
                    {viewingFirma.vertragslaufzeit && (
                      <div className="detail-item">
                        <FileText size={18} />
                        <div>
                          <label>Laufzeit</label>
                          <span>{viewingFirma.vertragslaufzeit}</span>
                        </div>
                      </div>
                    )}
                    {viewingFirma.kuendigungsfrist && (
                      <div className="detail-item">
                        <FileText size={18} />
                        <div>
                          <label>Kündigungsfrist</label>
                          <span>{viewingFirma.kuendigungsfrist}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {[1, 2, 3, 4, 5].some(num => viewingFirma[`ansprechperson${num}_name`]) && (
                <div className="detail-section">
                  <h4>Ansprechpersonen</h4>
                  <div className="ansprechpersonen-grid">
                    {[1, 2, 3, 4, 5].map(num => {
                      const name = viewingFirma[`ansprechperson${num}_name`];
                      const position = viewingFirma[`ansprechperson${num}_position`];
                      const telefon = viewingFirma[`ansprechperson${num}_telefon`];
                      const email = viewingFirma[`ansprechperson${num}_email`];
                      
                      if (!name) return null;
                      
                      return (
                        <div key={num} className="ansprechperson-card">
                          <h5>{name}</h5>
                          {position && (
                            <div className="info-row">
                              <Briefcase size={14} />
                              <span>{position}</span>
                            </div>
                          )}
                          {telefon && (
                            <div className="info-row">
                              <Phone size={14} />
                              <a href={`tel:${telefon}`}>{telefon}</a>
                            </div>
                          )}
                          {email && (
                            <div className="info-row">
                              <Mail size={14} />
                              <a href={`mailto:${email}`}>{email}</a>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {viewingFirma.gewerke && viewingFirma.gewerke.length > 0 && (
                <div className="detail-section">
                  <h4>Gewerke</h4>
                  <div className="tags">
                    {viewingFirma.gewerke.map(g => (
                      <span key={g.id} className="tag tag-gewerk">{g.name}</span>
                    ))}
                  </div>
                </div>
              )}

              {viewingFirma.filialen && viewingFirma.filialen.length > 0 && (
                <div className="detail-section">
                  <h4>Filialen ({viewingFirma.filialen.length})</h4>
                  <div className="filialen-detail-grid">
                    {viewingFirma.filialen.map(f => (
                      <div key={f.id} className="filiale-detail-card">
                        <span className="filiale-badge">{f.gebiet}</span>
                        <div>
                          <strong>{f.filialnummer}</strong> - {f.name}
                          {f.ort && <div className="filiale-ort-small">{f.ort}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewingFirma.notiz && (
                <div className="detail-section">
                  <h4>Notizen</h4>
                  <p className="notiz-text">{viewingFirma.notiz}</p>
                </div>
              )}

              {viewingFirma.dokumente && viewingFirma.dokumente.length > 0 && (
                <div className="detail-section">
                  <h4>Dokumente ({viewingFirma.dokumente.length})</h4>
                  <div className="dokumente-list">
                    {viewingFirma.dokumente.map(dok => (
                      <div key={dok.id} className="dokument-item">
                        <FileText size={18} />
                        <span className="dokument-name">{dok.filename}</span>
                        <span className="dokument-date">
                          {new Date(dok.uploaded_at).toLocaleDateString('de-DE')}
                        </span>
                        <a 
                          href={`/api/firmen/${viewingFirma.id}/dokumente/${dok.id}`}
                          download={dok.filename}
                          className="icon-btn"
                        >
                          <Download size={16} />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal (BEARBEITEN) */}
      {showModal && (
        <div 
          className="modal-overlay" 
          onMouseDown={(e) => {
            // Merke ob mousedown auf dem Overlay war
            modalOverlayMouseDown.current = (e.target === e.currentTarget);
          }}
          onClick={(e) => {
            // Nur schließen wenn mousedown UND mouseup auf dem Overlay waren
            if (e.target === e.currentTarget && modalOverlayMouseDown.current) {
              handleCloseModal();
            }
            modalOverlayMouseDown.current = false;
          }}
        >
          <div 
            ref={editModalRef}
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
          >
            <div className="modal-header">
              <h2>{editingFirma ? 'Firma bearbeiten' : 'Neue Firma'}</h2>
              <button type="button" onClick={handleCloseModal} className="close-btn">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="modal-body">
              <div className="form-section">
                <h4>Grunddaten</h4>
                <div className="form-row">
                  <div className="form-field">
                    <label>Firmenname *</label>
                    <input
                      ref={firstInputRef}
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label>Adresse</label>
                    <input
                      type="text"
                      name="adresse"
                      value={formData.adresse}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-row form-row-2">
                  <div className="form-field">
                    <label>Telefon</label>
                    <input
                      type="tel"
                      name="telefon"
                      value={formData.telefon}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-field">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-row form-row-2">
                  <div className="form-field">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="notfall_service"
                        checked={formData.notfall_service}
                        onChange={handleInputChange}
                      />
                      <span>24/7 Notfall-Service verfügbar</span>
                    </label>
                  </div>
                  <div className="form-field">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="rahmenvertrag"
                        checked={formData.rahmenvertrag}
                        onChange={handleInputChange}
                      />
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FileText size={16} />
                        Rahmenvertrag vorhanden
                      </span>
                    </label>
                  </div>
                </div>

                <div className="form-row form-row-2">
                  <div className="form-field">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="ist_favorit"
                        checked={formData.ist_favorit}
                        onChange={handleInputChange}
                      />
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Star size={16} fill={formData.ist_favorit ? "#fbbf24" : "none"} color="#fbbf24" />
                        Als Favorit markieren
                      </span>
                    </label>
                  </div>
                  <div className="form-field">
                    <label>Priorität</label>
                    <select
                      name="prioritaet"
                      value={formData.prioritaet}
                      onChange={handleInputChange}
                    >
                      <option value="bevorzugt">🟢 Bevorzugt - Erste Wahl</option>
                      <option value="standard">🟡 Standard - Normale Wahl</option>
                      <option value="backup">🟠 Backup - Nur wenn andere nicht verfügbar</option>
                      <option value="notfall">🔴 Nur im Notfall - Letzte Option</option>
                      <option value="keine">⚪ Keine Bewertung</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Vertragsdaten</h4>
                <div className="form-row form-row-3">
                  <div className="form-field">
                    <label>Vertragsnummer</label>
                    <input
                      type="text"
                      name="vertragsnummer"
                      value={formData.vertragsnummer}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-field">
                    <label>Laufzeit</label>
                    <input
                      type="text"
                      name="vertragslaufzeit"
                      value={formData.vertragslaufzeit}
                      onChange={handleInputChange}
                      placeholder="z.B. 12 Monate"
                    />
                  </div>
                  <div className="form-field">
                    <label>Kündigungsfrist</label>
                    <input
                      type="text"
                      name="kuendigungsfrist"
                      value={formData.kuendigungsfrist}
                      onChange={handleInputChange}
                      placeholder="z.B. 3 Monate"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Ansprechpersonen (bis zu 5)</h4>
                {[1, 2, 3, 4, 5].map(num => (
                  <div key={num} className="ansprechperson-group">
                    <h5>Ansprechperson {num}</h5>
                    <div className="form-row form-row-4">
                      <div className="form-field">
                        <label>Name</label>
                        <input
                          type="text"
                          name={`ansprechperson${num}_name`}
                          value={formData[`ansprechperson${num}_name`]}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="form-field">
                        <label>Position</label>
                        <input
                          type="text"
                          name={`ansprechperson${num}_position`}
                          value={formData[`ansprechperson${num}_position`]}
                          onChange={handleInputChange}
                          placeholder="z.B. Geschäftsführer, Verkauf..."
                        />
                      </div>
                      <div className="form-field">
                        <label>Telefon</label>
                        <input
                          type="tel"
                          name={`ansprechperson${num}_telefon`}
                          value={formData[`ansprechperson${num}_telefon`]}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="form-field">
                        <label>Email</label>
                        <input
                          type="email"
                          name={`ansprechperson${num}_email`}
                          value={formData[`ansprechperson${num}_email`]}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>
                ))}
				</div>

              <div className="form-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h4 style={{ margin: 0 }}>Gewerke</h4>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, gewerk_ids: gewerke.map(g => g.id) }))}
                      style={{
                        padding: '8px 16px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Alle auswählen
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, gewerk_ids: [] }))}
                      style={{
                        padding: '8px 16px',
                        background: 'white',
                        color: '#666',
                        border: '2px solid #e0e0e0',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Alle abwählen
                    </button>
                  </div>
                </div>
                <div style={{
                  padding: '4px 0',
                  marginBottom: '12px',
                  fontSize: '13px',
                  color: '#666'
                }}>
                  {formData.gewerk_ids.length} von {gewerke.length} Gewerken ausgewählt
                </div>
                <div className="checkbox-grid">
                  {gewerke.map(g => (
                    <label key={g.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.gewerk_ids.includes(g.id)}
                        onChange={() => handleMultiSelect('gewerk_ids', g.id)}
                      />
                      <span>{g.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h4 style={{ margin: 0 }}>Filialen</h4>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, filiale_ids: filialen.map(f => f.id) }))}
                      style={{
                        padding: '8px 16px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Alle auswählen
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, filiale_ids: [] }))}
                      style={{
                        padding: '8px 16px',
                        background: 'white',
                        color: '#666',
                        border: '2px solid #e0e0e0',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Alle abwählen
                    </button>
                  </div>
                </div>
                <div style={{
                  padding: '4px 0',
                  marginBottom: '12px',
                  fontSize: '13px',
                  color: '#666'
                }}>
                  {formData.filiale_ids.length} von {filialen.length} Filialen ausgewählt
                </div>
                <div className="checkbox-grid">
                  {filialen.map(f => (
                    <label key={f.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.filiale_ids.includes(f.id)}
                        onChange={() => handleMultiSelect('filiale_ids', f.id)}
                      />
                      <span>{f.filialnummer} - {f.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-section">
                <h4>Notizen</h4>
                <div className="form-row">
                  <div className="form-field">
                    <textarea
                      name="notiz"
                      value={formData.notiz}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="Zusätzliche Informationen..."
                    />
                  </div>
                </div>
              </div>

              {editingFirma && (
                <div className="form-section">
                  <h4>Dokumente ({editingFirma.dokumente?.length || 0})</h4>
                  
                  <div className="upload-section">
                    <label className="upload-label">
                      <Upload size={20} />
                      <span>{uploadingFile ? 'Wird hochgeladen...' : 'Datei hochladen'}</span>
                      <input
                        type="file"
                        onChange={handleFileUpload}
                        disabled={uploadingFile}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>

                  {editingFirma.dokumente && editingFirma.dokumente.length > 0 && (
                    <div className="dokumente-list">
                      {editingFirma.dokumente.map(dok => (
                        <div key={dok.id} className="dokument-item">
                          <div className="dokument-info">
                            <FileText size={20} className="dokument-icon" />
                            <div className="dokument-details">
                              <span className="dokument-name">{dok.filename}</span>
                              <span className="dokument-date">
                                Hochgeladen am {new Date(dok.uploaded_at).toLocaleDateString('de-DE', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          </div>
                          <div className="dokument-actions">
                            <button
                              type="button"
                              onClick={() => handleFileDownload(dok.id, dok.filename)}
                              className="icon-btn"
                              title="Herunterladen"
                            >
                              <Download size={18} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleFileDelete(dok.id, dok.filename)}
                              className="icon-btn icon-btn-danger"
                              title="Löschen"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {(!editingFirma.dokumente || editingFirma.dokumente.length === 0) && (
                    <p className="no-documents">Noch keine Dokumente hochgeladen</p>
                  )}
                </div>
              )}

              {!editingFirma && (
                <div className="form-section">
                  <p className="info-message">
                    <AlertCircle size={18} />
                    Dokumente können nach dem Speichern der Firma hochgeladen werden.
                  </p>
                </div>
              )}
              </div>

              <div className="modal-footer">
                <button type="button" onClick={handleCloseModal} className="btn-secondary">
                  Abbrechen
                </button>
                <button type="submit" className="btn-primary">
                  <Save size={20} />
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filialen Modal */}
      {showFilialeModal && (
        <div 
          className="modal-overlay" 
          onMouseDown={(e) => {
            modalOverlayMouseDown.current = (e.target === e.currentTarget);
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && modalOverlayMouseDown.current) {
              handleCloseFilialeModal();
            }
            modalOverlayMouseDown.current = false;
          }}
        >
          <div 
            ref={filialeModalRef}
            className="modal-content modal-filialen" 
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
          >
            <div className="modal-header">
              <h2>Filialen verwalten</h2>
              <button onClick={handleCloseFilialeModal} className="close-btn">
                <X size={24} />
              </button>
            </div>

            <div className="filialen-management">
              <div className="filialen-header">
                <h3>{editingFiliale ? 'Filiale bearbeiten' : 'Neue Filiale hinzufügen'}</h3>
              </div>

              <form onSubmit={handleFilialeSubmit} className="filiale-form">
                <div className="form-row form-row-2">
                  <div className="form-field">
                    <label>Gebiet *</label>
                    <select
                      ref={filialeFirstInputRef}
                      name="gebiet"
                      value={filialeFormData.gebiet}
                      onChange={handleFilialeInputChange}
                      required
                    >
                      <option value="">Bitte wählen</option>
                      <option value="Sd">Sd (Süd)</option>
                      <option value="Ost">Ost</option>
                      <option value="Nord">Nord</option>
                      <option value="West">West</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Filialnummer *</label>
                    <input
                      type="text"
                      name="filialnummer"
                      value={filialeFormData.filialnummer}
                      onChange={handleFilialeInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label>Filialname *</label>
                    <input
                      type="text"
                      name="name"
                      value={filialeFormData.name}
                      onChange={handleFilialeInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label>Adresse</label>
                    <input
                      type="text"
                      name="adresse"
                      value={filialeFormData.adresse}
                      onChange={handleFilialeInputChange}
                    />
                  </div>
                </div>

                <div className="form-row form-row-2">
                  <div className="form-field">
                    <label>PLZ</label>
                    <input
                      type="text"
                      name="plz"
                      value={filialeFormData.plz}
                      onChange={handleFilialeInputChange}
                    />
                  </div>
                  <div className="form-field">
                    <label>Ort</label>
                    <input
                      type="text"
                      name="ort"
                      value={filialeFormData.ort}
                      onChange={handleFilialeInputChange}
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={() => {
                    setEditingFiliale(null);
                    setFilialeFormData({
                      gebiet: '',
                      filialnummer: '',
                      name: '',
                      adresse: '',
                      plz: '',
                      ort: ''
                    });
                    handleCloseFilialeModal();
                  }} className="btn-secondary">
                    Abbrechen
                  </button>
                  <button type="submit" className="btn-primary">
                    <Save size={20} />
                    {editingFiliale ? 'Aktualisieren' : 'Hinzufügen'}
                  </button>
                </div>
              </form>

              <div className="filialen-list">
                <h3>Alle Filialen ({filialen.length})</h3>
                <div className="filialen-table">
                  {filialen.map(f => (
                    <div key={f.id} className="filiale-row">
                      <div className="filiale-info">
                        <span className="filiale-badge">{f.gebiet}</span>
                        <span className="filiale-nummer">{f.filialnummer}</span>
                        <span className="filiale-name">{f.name}</span>
                        {f.ort && <span className="filiale-ort">{f.ort}</span>}
                      </div>
                      <div className="filiale-actions">
                        <button onClick={() => handleOpenFilialeModal(f)} className="icon-btn">
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleFilialeDelete(f.id, f.name)} className="icon-btn icon-btn-danger">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Gewerke Management Modal */}
      {showGewerkeModal && (
        <div 
          className="modal-overlay" 
          onMouseDown={(e) => {
            modalOverlayMouseDown.current = (e.target === e.currentTarget);
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && modalOverlayMouseDown.current) {
              handleCloseGewerkeModal();
            }
            modalOverlayMouseDown.current = false;
          }}
        >
          <div 
            ref={gewerkeModalRef}
            className="modal-content modal-filialen" 
            onClick={(e) => e.stopPropagation()} 
            style={{ margin: '20px' }}
            tabIndex={-1}
          >
            <div className="modal-header">
              <h2>Gewerke verwalten</h2>
              <button onClick={handleCloseGewerkeModal} className="close-btn">
                <X size={24} />
              </button>
            </div>

            <div className="filialen-management">
              <div className="filialen-header">
                <h3>{editingGewerk ? 'Gewerk bearbeiten' : 'Neues Gewerk hinzufügen'}</h3>
              </div>

              <form onSubmit={handleGewerkeSubmit} className="filiale-form">
                <div className="form-row">
                  <div className="form-field">
                    <label>Name *</label>
                    <input
                      ref={gewerkeFirstInputRef}
                      type="text"
                      name="name"
                      value={gewerkeFormData.name}
                      onChange={handleGewerkeInputChange}
                      required
                      placeholder="z.B. Elektriker"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={() => {
                    setEditingGewerk(null);
                    setGewerkeFormData({ name: '', beschreibung: '' });
                    handleCloseGewerkeModal();
                  }} className="btn-secondary">
                    Abbrechen
                  </button>
                  <button type="submit" className="btn-primary">
                    <Save size={20} />
                    {editingGewerk ? 'Aktualisieren' : 'Hinzufügen'}
                  </button>
                </div>
              </form>

              <div className="filialen-list">
                <h3>Alle Gewerke ({gewerke.length})</h3>
                <div className="filialen-table">
                  {gewerke.map(gewerk => (
                    <div key={gewerk.id} className="filiale-row">
                      <div className="filiale-info">
                        <span className="filiale-name">{gewerk.name}</span>
                      </div>
                      <div className="filiale-actions">
                        <button onClick={() => handleOpenGewerkeModal(gewerk)} className="icon-btn">
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleGewerkeDelete(gewerk.id, gewerk.name)} className="icon-btn icon-btn-danger">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;